import React, { createContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUniqueId } from '../../../hooks/use-unique-id'
import { queryClient } from '../../../queryClient'
import { useRequiredContext } from '../../../utils/use-required-context'
import { GroupedSearchOptions, SearchDropdownProps, SearchOption, SearchOptionGroup, UNGROUPED } from './index'
import { mergeDefined } from '../../../helpers/merge-defined'
import { defaultFilter } from 'cmdk'
import { useDebouncedValue } from "../../../hooks/use-debounce"
import { removeNullish } from "../../../utils/typescript"

/** Internal SearchDropdown state shared context */
type SearchDropdownInternal = {
  /** search input value: only for the input, never debounced */
  searchInputValue: string
  /** search value to be used for display and filters - possibly debounced */
  search: string
  onSearch: (search: string) => void
  open: boolean
  onOpen: (open: boolean) => void
  openAndPropagate: () => void
  onSelect: (value: string | null) => void
  selectedLabelAsync: ReturnType<typeof useSelectedLabelAsync>
  labels: NonNullable<Required<SearchDropdownProps['labelConfig']>>
  variant: NonNullable<SearchDropdownProps['variant']>
  derivedOptions: ReturnType<typeof useDerivedOptions>
  filterFn: SearchDropdownProps["filterFn"]
  props: SearchDropdownProps
  inputRef: React.MutableRefObject<HTMLInputElement | null>
}

const SearchDropdownInternalContext = createContext<SearchDropdownInternal | null>(null)

/** Only for internal use. Note that subscribing to this will currently cause rerenders on every update. */
export const useSearchDropdownController = () => useRequiredContext(SearchDropdownInternalContext)

export function SearchDropdownController(props: SearchDropdownProps & {
  children: React.ReactNode
}) {
  const id = useUniqueId()
  const [ debouncedSearch, searchInputValue, setSearchInputValue ] = useSearchInputHandling(props)
  const [ open, onOpen, openAndPropagate ] = usePopoverOpenHandling(props)
  const filterFn = props.filterFn ?? defaultFilter
  const derivedOptions = useDerivedOptions({
    options: props.options,
    shouldFilter: props.shouldFilter ?? true,
    filterFn,
    search: debouncedSearch
  })
  const cachedOptionLabels = useCachedOptionLabels(derivedOptions.allOptions)
  const selectedLabelAsync = useSelectedLabelAsync(id, props.value, derivedOptions.allOptions, cachedOptionLabels.current, props.getLabelForOptionValue)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const onSelect = async (currentValue: string | null) => {
    props.onChange?.(currentValue)
    onOpen(false)
  }

  useEffect(() => {
    async function syncSearchInputWithLabel() {
      if (props.value && props.syncSearchWithLabel) {
        try {
          setSearchInputValue(await selectedLabelAsync.fetch(props.value))
        } catch (e) {
          console.error("SearchDropdown: failed fetching selected label", e)
          setSearchInputValue(props.value || "")
        }
      } else {
        setSearchInputValue("")
      }
    }
    void syncSearchInputWithLabel()
  }, [ props.value, props.syncSearchWithLabel ])

  const variant = props.variant ?? "default"

  const labels = mergeDefined({
    placeholder: "",
    noOptionsFound: "No options found.",
    loadingOptions: "Loading options...",
    errorFetchingOptions: "Error fetching options.",
  }, props.labelConfig)

  return (
    <SearchDropdownInternalContext.Provider value={{
      labels,
      variant,
      searchInputValue,
      search: debouncedSearch,
      onSearch: setSearchInputValue,
      open,
      onOpen,
      onSelect,
      selectedLabelAsync,
      openAndPropagate,
      derivedOptions,
      filterFn,
      props,
      inputRef
    }}>
      {props.children}
    </SearchDropdownInternalContext.Provider>
  )
}

function useSearchInputHandling(props: SearchDropdownProps) {
  if ((props.search !== undefined) !== (props.onSearch !== undefined)) {
    throw new Error("SearchDropdown: 'search' and 'onSearch' must be both defined or both undefined.")
  }
  const [ localInputValue, setLocalInputValue ] = useState('')

  const inputValue = props.search ?? localInputValue
  const debouncedValue = useDebouncedValue(inputValue, props.debounceTime ?? 0)

  const setInputValue = (value: string) => {
    props.onSearch ? props.onSearch(value) : setLocalInputValue(value)
  }

  return [ debouncedValue, inputValue, setInputValue ] as const
}

function usePopoverOpenHandling(props: SearchDropdownProps) {
  const [ open, setOpen ] = useState(false)

  const onOpen = (open: boolean) => {
    setOpen(open)
    props.onOpenChange?.(open)
  }

  const openAndPropagate = () => {
    setOpen(true)
    props.onOpenChange?.(true)
  }

  return [ open, onOpen, openAndPropagate ] as const
}

function useDerivedOptions(args: {
  options: SearchOption[] | GroupedSearchOptions
  shouldFilter: boolean
  filterFn: NonNullable<SearchDropdownProps["filterFn"]>
  search: string
}) {
  const { options, shouldFilter, filterFn, search } = args
  return useMemo(() => {
    const groupedOptions = normalizeToGroupedSearchOptions(options)
    const filteredGroups = filterGroupedOptions(groupedOptions)

    const optionRows = toOptionRows(filteredGroups)
    const allOptions = Array.from(filteredGroups).flatMap(([, g]) => g.options)

    return {
      optionRows,
      allOptions
    }

    function filterGroupedOptions(groupedOptions: GroupedSearchOptions): GroupedSearchOptions {
      if (!shouldFilter || !search) return groupedOptions

      const filtered: GroupedSearchOptions = new Map()
      for (const [ key, group ] of groupedOptions) {
        const filteredOptions = group.options
          .map(opt => ({
            option: opt,
            score: filterFn(opt.value, search, removeNullish([ opt.label, group.label ]))
          }))
          .filter(x => x.score > 0)
          .sort((a, b) => b.score - a.score)
          .map(x => x.option)

        if (filteredOptions.length === 0) continue
        filtered.set(key, {
          label: group.label,
          icon: group.icon,
          options: filteredOptions
        })
      }

      return filtered
    }
  }, [ options, shouldFilter, filterFn, search ])
}

function normalizeToGroupedSearchOptions(providedOptions: SearchOption[] | GroupedSearchOptions) {
  if (providedOptions instanceof Map) return providedOptions
  else {
    const opts: GroupedSearchOptions = new Map()
    opts.set(UNGROUPED, {
      options: providedOptions
    })
    return opts
  }
}

/** Flatten back to rows (but sorted) for virtualization */
export type OptionRow
  = { type: "option", option: SearchOption, key: string }
  | { type: "group", group: SearchOptionGroup, key: string }

function toOptionRows(groupedOptions: GroupedSearchOptions): OptionRow[] {
  const rows: OptionRow[] = []

  for (const [ key, group ] of groupedOptions) {
    if (key !== UNGROUPED) {
      rows.push({
        type: "group",
        group,
        key
      })
    }
    for (const option of group.options) {
      rows.push({
        type: "option",
        option,
        key: option.value
      })
    }
  }

  return rows
}

function useCachedOptionLabels(options: SearchOption[]) {
  const cache = useRef<Map<string, string>>(new Map())
  for (const option of options) {
    if (cache.current.has(option.value)) continue
    cache.current.set(option.value, option.label)
  }
  return cache
}

type SelectedLabelResult
  = { type: "success", value: string }
  | { type: "loading" }
  | { type: "error", error: Error }

function useSelectedLabelAsync(
  id: string,
  currentValue: string | null,
  allOptions: SearchOption[],
  cachedOptionLabels: Map<string, string>,
  getLabelForOptionValue?: (value: string) => Promise<string>
) {
  const queryOptions = (value: string | null) => ({
    queryKey: ["search-dropdown-label", id, value, ...allOptions.map(o => o.value)],
    queryFn: async (): Promise<string> => {
      if (!value) return ""
      if (cachedOptionLabels.has(value)) return cachedOptionLabels.get(value)!
      const option = allOptions.find(o => o.value === value)
      if (option) return option.label
      if (getLabelForOptionValue) return await getLabelForOptionValue(value)
      throw new Error(`SearchDropdown getLabelForOptionValue not defined`)
    },
    gcTime: 10_000,
    retry: false
  })

  const queryResult = useQuery(queryOptions(currentValue))
  const fetch = (value: string | null) => queryClient.fetchQuery(queryOptions(value))

  const result: SelectedLabelResult = (() => {
    if (queryResult.error) return { type: "error", error: queryResult.error } as const
    if (queryResult.data === undefined) return { type: "loading" } as const
    return { type: "success", value: queryResult.data } as const
  })()

  return { result, fetch }
}
